// Imports
import React, { useRef,useState, useEffect, useMemo, useCallback } from "react";
import {
  message
} from "antd";
import "tippy.js/dist/tippy.css";
import { SyncOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "../../../components/reusable/LoadingSpinner";
import ConfirmationModal from "../../../components/reusable/ConfirmationModal";
import DateRangePicker from "../../../components/reusable/DateRangePicker/DateRangePicker";
import dayjs, { Dayjs } from "dayjs";
import MultiMetricBarChart from "../../../components/reusable/MultiMetricBarChart";
import CampaignTable from "../../../components/reusable/CampaignTable/CampaignTable";
import { saveAs } from "file-saver";
import {CustomIcon} from "../../../components/reusable/CustomIcon";
import StyledInput from "../../../components/reusable/StyledInput";
import { SelectMenu } from "src/dashboards/salesProductAnalytics/components/salesProductShared";
import { Trash2, Download, Archive, CirclePlus, ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLanguage } from "src/hooks/useLanguage";
import { formatNumber, getNumberLocale, wrapBidiIsolate } from "src/utils/numberFormat";
import { useDynamicTextTranslator } from "src/utils/dynamicTextTranslation";
import { DashboardNarrativePanel, DefinitionTooltip } from "src/components/common";
import { useDashboardNarrative } from "src/hooks/useDashboardNarrative";
import { useAsyncResource } from "src/dashboards/paidMedia/hooks/useAsyncResource";
import {
  engagementOverviewService,
  type EngagementAchievementsData,
} from "src/services/EngagementOverviewAPI";
import {
  campaignPerformanceService,
  type CampaignComparisonData,
  type CampaignListRow,
  type CampaignPagination,
} from "src/services/CampaignPerformanceAPI";
import { BusinessImpactSection } from "../Overview/components/shared";
import { getEngagementTooltipInfo } from "../Overview/components/shared/getEngagementTooltipInfo";
import { parseApiNumber } from "../utils/parseApiNumber";
import { mapAchievements } from "../Overview/utils/engagementOverviewMappers";
import StyledDropdown from "../../../components/reusable/StyledDropdown";
import CampaignPerformance from "./CampaignPerformance";
import "../Overview/components/shared/EngagementOverview.scss";

/**
 * The table's scope/filter/search/pagination all run client-side, so the whole
 * cohort is fetched at once. 1000 is the endpoint's ceiling for `page_size`.
 */
const CAMPAIGN_LIST_PAGE_SIZE = 1000;

// Constants and Types
interface Campaign {
  campaign_id: string;
  campaign_name: string;
  audience: string;
  created_at: string;
  updated_at: string;
  delivered: number;
  opens: number;
  clicks: number;
  bounces: number;
  open_rate: number;
  click_rate: number;
  bounce_rate: number;
  conversion_rate: number;
  status: string;
  [key: string]: string | number;
}


const metricOptionsBase = [
  { value: "delivered", key: "delivered", labelKey: "delivered" },
  { value: "openRate", key: "open_rate", labelKey: "openRate" },
  { value: "clickRate", key: "ctr", labelKey: "ctr" },
  { value: "bounceRate", key: "bounce_rate", labelKey: "bounceRate" },
  { value: "conversionRate", key: "conversion_rate", labelKey: "conversionRate" },
  { value: "unsubscribeRate", key: "unsubscribe_rate", labelKey: "unsubscribeRate" },
];

/**
 * Flattens a `campaigns-list` row onto the shape this screen's columns, export
 * and filters already read. `Campaign` carries an index signature, so the extra
 * metric fields ride along and the generic `readCampaignField`/
 * `readCampaignNumber` helpers keep working unchanged.
 *
 * `sent_at` and `scheduled` are mutually exclusive — the inapplicable one is
 * the string `"-"`, which is normalised to `""` here so the existing
 * "-"-rendering in the columns stays the single place that decides how an
 * absent date looks.
 */
const mapCampaignListRow = (row: CampaignListRow): Campaign => {
  const metrics = row.metrics;
  const sentAt = !row.sent_at || row.sent_at === "-" ? "" : row.sent_at;
  const scheduled = !row.scheduled || row.scheduled === "-" ? "" : row.scheduled;

  return {
    campaign_id: row.campaign_id,
    campaign_name: row.campaign_name ?? "",
    audience: row.audience ?? "",
    created_at: sentAt,
    updated_at: sentAt,
    sent_at: sentAt,
    scheduled,
    scheduledAt: scheduled,
    source: row.source ?? "",
    status: row.status ?? "",
    // Rates arrive as percentages already — never scale them here.
    delivered: metrics?.emails_sent ?? 0,
    opens: metrics?.opens ?? 0,
    clicks: 0,
    bounces: 0,
    open_rate: metrics?.open_rate ?? 0,
    click_rate: metrics?.click_rate ?? 0,
    bounce_rate: metrics?.bounce_rate ?? 0,
    conversion_rate: metrics?.conversion_rate ?? 0,
    conversions: metrics?.conversions ?? 0,
    // Row-level `audience` is the list name(s); this is the resolved list size.
    audience_size: metrics?.audience ?? 0,
    total_users: metrics?.total_users ?? 0,
  };
};

const campaignDashboardTranslationKeys = [
  "openRate",
  "clickRate",
  "ctr",
  "conversionRate",
  "bounceRate",
  "delivered",
  "unsubscribeRate",
  "shareRate",
  "deleteCampaign",
  "archiveCampaign",
  "unarchiveCampaign",
  "campaignAction",
  "delete",
  "archive",
  "unarchive",
  "confirm",
  "deleting",
  "archiving",
  "unarchiving",
  "processing",
  "noCampaignsAvailable",
  "errorFetchingCampaigns",
  "defaultCampaignName",
  "campaignDeletedSuccess",
  "campaignArchivedSuccess",
  "campaignUnarchivedSuccess",
  "campaignsDeletedSuccess",
  "campaignsArchivedSuccess",
  "campaignsUnarchivedSuccess",
  "failedToActionCampaign",
  "errorActioningCampaign",
  "exportFilename",
  "singleExportFilename",
  "selectAtLeastOneDelete",
  "selectAtLeastOneArchive",
  "campaignName",
  "sentOn",
  "scheduledAt",
  "audience",
  "actions",
  "tooltipUnarchive",
  "tooltipArchive",
  "tooltipDelete",
  "tooltipDownload",
  "confirmMany",
  "thisCampaign",
  "confirmOne",
  "bannerText",
  "dismissSummary",
  "campaignPerformance",
  "trackEmailCampaigns",
  "archivedCampaigns",
  "activeCampaigns",
  "createNewCampaign",
  "viewActiveCampaigns",
  "viewArchivedCampaigns",
  "search",
  "chartTitle",
  "cancel",
  "atWord",
  "achievementsSubtitle",
  "filterCampaignName",
  "filterAllCampaigns",
  "filterSource",
  "filterStatus",
  "filterAll",
  "filterAllSources",
  "filterAllStatus",
  "scheduled",
  "unscheduled",
  "whatsapp",
  "email",
  "pageOf",
  "tabActive",
  "tabArchive",
  "export",
  "campaignComparison",
  "achievementsLoadFailed",
] as const;

type CampaignDashboardTranslationKey =
  typeof campaignDashboardTranslationKeys[number];

type CampaignDashboardText = Record<CampaignDashboardTranslationKey, string>;

// Helper Functions
const getModalProps = (
  type: string,
  t: CampaignDashboardText,
) => {
  switch (type) {
    case "delete":
      return {
        title: t.deleteCampaign,
        icon: "/Icons/delete-icon.svg",
        confirmText: t.delete,
        loadingText: t.deleting,
      };
    case "archive":
      return {
        title: t.archiveCampaign,
        icon: "/Icons/archive-icon.svg",
        confirmText: t.archive,
        loadingText: t.archiving,
      };
    case "unarchive":
      return {
        title: t.unarchiveCampaign,
        icon: "/Icons/unarchive-icon.svg",
        confirmText: t.unarchive,
        loadingText: t.unarchiving,
      };
    default:
      return {
        title: t.campaignAction,
        icon: "",
        confirmText: t.confirm,
        loadingText: t.processing,
      };
  }
};

// `Campaign` carries an index signature, so the overview endpoint can return
// fields the interface doesn't declare. Read channel/status through these
// helpers rather than assuming a shape: if the backend doesn't send them the
// UI degrades to a neutral placeholder instead of inventing a value.
const readCampaignField = (campaign: Campaign, keys: string[]): string => {
  for (const key of keys) {
    const value = campaign[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  return "";
};

const getCampaignSource = (campaign: Campaign): string =>
  readCampaignField(campaign, ["source", "medium", "channel", "campaign_type"]);

const getCampaignStatus = (campaign: Campaign): string =>
  readCampaignField(campaign, ["status"]);

const SOURCE_DISPLAY_LABEL: Record<string, string> = {
  email: "Email",
  whatsapp: "WhatsApp",
};

const STATUS_DISPLAY_LABEL: Record<string, string> = {
  draft: "Draft",
  scheduled: "Scheduled",
  sent: "Sent",
  active: "Active",
  archive: "Archived",
};

const formatSourceLabel = (source: string): string =>
  SOURCE_DISPLAY_LABEL[source.toLowerCase()] ?? source;

const formatStatusLabel = (status: string): string =>
  STATUS_DISPLAY_LABEL[status.toLowerCase()] ?? status;

const getCampaignScheduleStatus = (campaign: Campaign): "scheduled" | "unscheduled" => {
  const scheduledValue = campaign.scheduledAt ?? campaign.scheduled_for ?? campaign.scheduled_at;
  if (scheduledValue === undefined || scheduledValue === null) {
    return "unscheduled";
  }
  const text = String(scheduledValue).trim();
  if (!text || text === "-") {
    return "unscheduled";
  }
  return "scheduled";
};

// Maps a channel value onto a known badge colour, falling back to a neutral
// variant so an unrecognised channel still renders legibly.
const normalizeBadgeVariant = (value: string): string => {
  const normalized = value.trim().toLowerCase();
  if (normalized.includes("whatsapp")) {
    return "whatsapp";
  }
  if (normalized.includes("email") || normalized.includes("mail")) {
    return "email";
  }
  return "neutral";
};

// Distinct, non-empty values in first-seen order — drives the Source/Status
// filter options so they only ever offer values the data actually contains.
const collectFilterOptions = (
  campaigns: Campaign[],
  read: (_campaign: Campaign) => string
): string[] => Array.from(new Set(campaigns.map(read).filter(Boolean)));

// Component
const CampaignDashboard: React.FC = () => {
  const { t: translate, i18n } = useTranslation();
  useLanguage();
  const t: CampaignDashboardText = useMemo(
    () =>
      campaignDashboardTranslationKeys.reduce((acc, key) => {
        acc[key] = translate(`campaignDashboard.${key}`);
        return acc;
      }, {} as CampaignDashboardText),
    [translate, i18n.resolvedLanguage, i18n.language],
  );
  const metricOptions = useMemo(
    () =>
      metricOptionsBase.map(metric => ({
        ...metric,
        label: t[metric.labelKey as keyof typeof t] as string,
      })),
    [t],
  );
  const navigate = useNavigate();
  const isArabic = i18n.language?.startsWith("ar");
  const numberLocale = useMemo(
    () => getNumberLocale(i18n.language),
    [i18n.language]
  );
  const dateFormatter = useMemo(
    () => ({
      format: (date: Date) => {
        const dd = String(date.getDate()).padStart(2, "0");
        const mm = String(date.getMonth() + 1).padStart(2, "0");
        return `${dd}-${mm}-${date.getFullYear()}`;
      },
    }),
    []
  );
  const timeFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(numberLocale, {
        hour: "numeric",
        minute: "2-digit",
      }),
    [numberLocale]
  );
  const formatDateTime = (dateString: string) => {
    if (!dateString || dateString === "-" || dateString.trim() === "") {
      return "-";
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return "-";
    }
    return `${dateFormatter.format(date)}\n${timeFormatter.format(date)}`;
  };

  const formatInteger = useCallback(
    (value: number) => formatNumber(value, {}, i18n.language),
    [i18n.language]
  );

  const formatPercentValue = useCallback(
    (value: number, fractionDigits = 2) => {
      const percentText = `${formatNumber(
        value,
        {
          minimumFractionDigits: fractionDigits,
          maximumFractionDigits: fractionDigits,
        },
        i18n.language
      )}%`;
      return isArabic ? wrapBidiIsolate(percentText) : percentText;
    },
    [i18n.language, isArabic]
  );

  // State
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>([
    "delivered",
    "openRate",
    "clickRate",
    "bounceRate",
    "conversionRate",
    "unsubscribeRate",
  ]);
  const selectAllRef = useRef<HTMLInputElement>(null);
 const [selectedCampaigns, setSelectedCampaigns] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [startDate, setStartDate] = useState(dayjs().subtract(3, "month").format("YYYY-MM-DD"));
const [endDate, setEndDate] = useState(dayjs().format("YYYY-MM-DD"));

  const [showArchived, setShowArchived] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(
    null
  );
  const [performanceCampaign, setPerformanceCampaign] = useState<{
    campaign_id: string;
    campaign_name: string;
  } | null>(null);
  const [pendingActionIds, setPendingActionIds] = useState<string[]>([]);
  const [actionType, setActionType] = useState<
    "archive" | "delete" | "unarchive" | null
  >(null);
  const [actionLoading, setActionLoading] = useState(false);
  // Header/toolbar filters. Empty string = "all" for each.
  const [campaignFilter, setCampaignFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  // Campaign Comparison: campaign_ids to plot. Empty = plot everything in scope.
  const [comparisonIds, setComparisonIds] = useState<string[]>([]);
  const [comparisonSources, setComparisonSources] = useState<string[]>([]);
  const [listPagination, setListPagination] = useState<CampaignPagination | null>(null);
  const pageSize = 6;

  const dynamicTextPool = useMemo(
    () =>
      campaigns.flatMap(campaign => [
        campaign.campaign_name,
        campaign.audience,
      ]),
    [campaigns]
  );

  const translateDynamicLabel = useDynamicTextTranslator(
    dynamicTextPool,
    i18n.language
  );

  // Derived Data
  // Campaigns in the current Active/Archive scope, before the dropdown filters.
  // Filter options are derived from this so they can never offer a value that
  // contradicts the selected tab (e.g. "archive" while viewing Active).
  const scopedCampaigns = useMemo(() => {
    return campaigns.filter(c =>
      showArchived ? c.status === "archive" : c.status !== "archive"
    );
  }, [campaigns, showArchived]);

  const filteredCampaigns = useMemo(() => {
    return scopedCampaigns.filter(c => {
      if (campaignFilter && c.campaign_name !== campaignFilter) {
        return false;
      }
      if (
        sourceFilter &&
        getCampaignSource(c).toLowerCase() !== sourceFilter.toLowerCase()
      ) {
        return false;
      }
      if (statusFilter && getCampaignScheduleStatus(c) !== statusFilter) {
        return false;
      }
      return true;
    });
  }, [scopedCampaigns, campaignFilter, sourceFilter, statusFilter]);

  const campaignNameOptions = useMemo(
    () => [
      { value: "", label: t.filterAllCampaigns },
      ...Array.from(new Set(scopedCampaigns.map(c => c.campaign_name).filter(Boolean))).map(
        name => ({
          value: name,
          label: translateDynamicLabel(name) || name,
        })
      ),
    ],
    [scopedCampaigns, translateDynamicLabel, t.filterAllCampaigns]
  );

  const sourceValues = useMemo(
    () => collectFilterOptions(scopedCampaigns, getCampaignSource),
    [scopedCampaigns]
  );

  const sourceOptions = useMemo(
    () => [
      { value: "", label: t.filterAllSources },
      { value: "WhatsApp", label: t.whatsapp },
      { value: "Email", label: t.email },
    ],
    [t.filterAllSources, t.whatsapp, t.email]
  );

  const statusOptions = useMemo(
    () => [
      { value: "", label: t.filterAllStatus },
      { value: "scheduled", label: t.scheduled },
      { value: "unscheduled", label: t.unscheduled },
    ],
    [t.filterAllStatus, t.scheduled, t.unscheduled]
  );

  // "What DataTram has helped you achieve" — same endpoint/mapper the
  // Engagements Overview uses, scoped to this page's date range + campaign filter.
  const achievementsRequest = useCallback(
    () =>
      engagementOverviewService.fetchAchievements({
        start_date: startDate,
        end_date: endDate,
        campaign_name: campaignFilter || null,
      }),
    [startDate, endDate, campaignFilter, i18n.resolvedLanguage, i18n.language]
  );

  const achievements = useAsyncResource<EngagementAchievementsData>(
    startDate && endDate ? achievementsRequest : null,
    t.achievementsLoadFailed
  );

  const businessImpact = useMemo(
    () => mapAchievements(translate, achievements.data),
    [achievements.data, translate]
  );

 
  const comparisonRequest = useCallback(
    () =>
      campaignPerformanceService.fetchCampaignComparison({
        start_date: startDate,
        end_date: endDate,
        source: null,
        campaign_name: campaignFilter || null,
      }),
    [startDate, endDate, campaignFilter, i18n.resolvedLanguage, i18n.language]
  );

  const comparison = useAsyncResource<CampaignComparisonData>(
    startDate && endDate ? comparisonRequest : null,
    t.errorFetchingCampaigns
  );

  const narrativeData = useMemo(() => {
    const blocks: Record<string, unknown> = {};
    if (achievements.data) {
      blocks.achievements = achievements.data;
    }
    if (filteredCampaigns.length > 0) {

      blocks.campaigns_list = {
        data: filteredCampaigns,
        pagination: listPagination,
      };
    }
    if (comparison.data) {
      blocks.comparison = comparison.data;
    }
    return blocks;
  }, [achievements.data, filteredCampaigns, listPagination, comparison.data]);

  const {
    narrative,
    loading: narrativeLoading,
    aiSummary,
  } = useDashboardNarrative({
    dashboardKey: "engagements_campaigns",
    tabKey: "overview",
    startDate,
    endDate,
    data: narrativeData,
    enabled: Boolean(achievements.data) || filteredCampaigns.length > 0,
  });


  const comparisonCampaigns = useMemo(() => {
    const rows = comparison.data?.campaigns ?? [];
    return rows.filter(row => {
      if (comparisonIds.length > 0 && !comparisonIds.includes(row.campaign_id)) {
        return false;
      }
      if (
        comparisonSources.length > 0 &&
        !comparisonSources.some(
          source => source.toLowerCase() === String(row.source ?? "").toLowerCase()
        )
      ) {
        return false;
      }
      return true;
    });
  }, [comparison.data, comparisonIds, comparisonSources]);

  const filteredChartData = useMemo(() => {
    const toChartNumber = (value: unknown): number => parseApiNumber(value) ?? 0;
    return comparisonCampaigns.map(row => ({
      name: translateDynamicLabel(row.campaign_name) || "-",
      delivered: toChartNumber(row.delivered),
      openRate: toChartNumber(row.open_rate),
      clickRate: toChartNumber(row.ctr),
      bounceRate: toChartNumber(row.bounce_rate),
      conversionRate: toChartNumber(row.conversion_rate),
      unsubscribeRate: toChartNumber(row.unsubscribe_rate),
    }));
  }, [comparisonCampaigns, translateDynamicLabel]);

  const comparisonOptions = useMemo(
    () =>
      scopedCampaigns.map(campaign => ({
        value: campaign.campaign_id,
        label: translateDynamicLabel(campaign.campaign_name) || campaign.campaign_name,
      })),
    [scopedCampaigns, translateDynamicLabel]
  );

  const comparisonSourceOptions = useMemo(
    () =>
      sourceValues.map(value => ({
        value,
        label: translateDynamicLabel(value) || value,
      })),
    [sourceValues, translateDynamicLabel]
  );


const fetchCampaignsOverview = async (start: string, end: string) => {
    if (!start || !end) {return;}
  try {
    setLoading(true);

    const { data, pagination } = await campaignPerformanceService.fetchCampaignsList({
      start_date: start,
      end_date: end,
      // Both scopes in one pass — the Active/Archive tab filters client-side.
      status: null,
      source: null,
      page: 1,
      page_size: CAMPAIGN_LIST_PAGE_SIZE,
      sort_by: "sent_at",
      sort_order: "desc",
    });

    const mapped = data.map(mapCampaignListRow);
    setCampaigns(mapped);
    setListPagination(pagination ?? null);

    localStorage.setItem(
      "campaignInfo",
      JSON.stringify(
        data.map(campaign => ({ id: campaign.campaign_id, name: campaign.campaign_name })),
      ),
    );
  } catch (err) {
    console.error(t.errorFetchingCampaigns, err);
    setCampaigns([]);
    setListPagination(null);
  } finally {
    setLoading(false);
  }
};
  const handleCampaignClick = (campaign: Campaign) => {
    setPerformanceCampaign({
      campaign_id: campaign.campaign_id,
      campaign_name: campaign.campaign_name,
    });
  };


  const handleDeleteCampaign = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setPendingActionIds([campaign.campaign_id]);
    setActionType("delete");
    setModalOpen(true);
  };

  const handleArchiveCampaign = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setPendingActionIds([campaign.campaign_id]);
    setActionType(campaign.status === "archive" ? "unarchive" : "archive");
    setModalOpen(true);
  };

  const handleCancel = () => {
    setModalOpen(false);
    setSelectedCampaign(null);
    setActionType(null);
  };

  const handleConfirm = async () => {
    if (!actionType) {return;}
    const campaignIds =
      pendingActionIds.length > 0
        ? pendingActionIds
        : selectedCampaign
          ? [selectedCampaign.campaign_id]
          : [];
    if (campaignIds.length === 0) {return;}
    setActionLoading(true);
    try {
      const isDelete = actionType === "delete";
      const isArchive = actionType === "archive";

      // WhatsApp ids come back in `not_found` — archive/delete do not cover
      // that channel yet, so report them rather than claiming success.
      const notFound = isDelete
        ? (await campaignPerformanceService.deleteCampaigns({ campaign_id: campaignIds }))
            .not_found
        : (
            await campaignPerformanceService.archiveCampaigns({
              campaign_id: campaignIds,
              archived: isArchive,
            })
          ).not_found;

      const actioned = campaignIds.filter(id => !notFound?.includes(id));

      if (notFound?.length) {
        message.warning(
          t.failedToActionCampaign.replace(
            "{{action}}",
            isDelete ? t.delete : isArchive ? t.archive : t.unarchive,
          ),
        );
      }

      if (actioned.length > 0) {
        if (actioned.length === 1) {
          const name =
            translateDynamicLabel(selectedCampaign?.campaign_name) ||
            translateDynamicLabel(campaigns.find(c => c.campaign_id === campaignIds[0])?.campaign_name) ||
            t.defaultCampaignName;
          message.success(
            isDelete
              ? t.campaignDeletedSuccess.replace("{{name}}", name)
              : isArchive
                ? t.campaignArchivedSuccess.replace("{{name}}", name)
                : t.campaignUnarchivedSuccess.replace("{{name}}", name),
          );
        } else {
          message.success(
            (isDelete
              ? t.campaignsDeletedSuccess
              : isArchive
                ? t.campaignsArchivedSuccess
                : t.campaignsUnarchivedSuccess
            ).replace("{{count}}", String(actioned.length)),
          );
        }

        fetchCampaignsOverview(startDate, endDate);
      } else if (!notFound?.length) {
        const actionLabel = isDelete ? t.delete : isArchive ? t.archive : t.unarchive;
        message.error(t.failedToActionCampaign.replace("{{action}}", actionLabel));
      }
    } catch (err) {
      console.error(err);
      const actionLabel =
        actionType === "delete"
          ? t.delete
          : actionType === "archive"
            ? t.archive
            : t.unarchive;
      message.error(t.errorActioningCampaign.replace("{{action}}", actionLabel));
    } finally {
      setActionLoading(false);
      setModalOpen(false);
      setSelectedCampaign(null);
      setPendingActionIds([]);
      setSelectedCampaigns([]);
      setActionType(null);
    }
  };

  const exportToCSV = (data: any[], filename: string) => {
  if (!data || data.length === 0) {return;}

  // Extract headers
  const headers = Object.keys(data[0]);
  const csvRows = [];

  // Add header row
  csvRows.push(headers.join(","));

  // Add data rows
  for (const row of data) {
    const values = headers.map(header => {
      const val = row[header];
      return typeof val === "string"
        ? `"${val.replace(/"/g, "\"\"")}"`
        : val;
    });
    csvRows.push(values.join(","));
  }

  const csvString = csvRows.join("\n");
  const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
  saveAs(blob, `${filename}.csv`);
};

// ðŸŸ¦ Button handler
const handleExport = () => {
  // If some campaigns are selected â†’ export those only
  const campaignsToExport =
    selectedCampaigns.length > 0
      ? filteredCampaigns.filter(c =>
          selectedCampaigns.includes(c.campaign_id)
        )
      : filteredCampaigns; // If none selected, export all

  exportToCSV(campaignsToExport, t.exportFilename);
};
const handleExportSingle = (campaign: Campaign) => {
  exportToCSV([campaign], `${t.singleExportFilename}${campaign.campaign_id}`);
};
const handleBulkDelete = () => {
  if (selectedCampaigns.length === 0) {
    message.info(t.selectAtLeastOneDelete);
    return;
  }
  setSelectedCampaign(null);
  setPendingActionIds(selectedCampaigns);
  setActionType("delete");
  setModalOpen(true);
};

const handleBulkArchive = () => {
  if (selectedCampaigns.length === 0) {
    message.info(t.selectAtLeastOneArchive);
    return;
  }
  const selectedSet = new Set(selectedCampaigns);
  const selectedRecords = campaigns.filter(c => selectedSet.has(c.campaign_id));
  const allArchived =
    selectedRecords.length > 0 && selectedRecords.every(c => c.status === "archive");
  setSelectedCampaign(null);
  setPendingActionIds(selectedCampaigns);
  setActionType(allArchived ? "unarchive" : "archive");
  setModalOpen(true);
};

const searchedCampaigns = useMemo(() => {
  const normalizedQuery = searchQuery.toLowerCase();
  return filteredCampaigns.filter(c =>
    c.campaign_name.toLowerCase().includes(normalizedQuery) ||
    (translateDynamicLabel(c.campaign_name) || "")
      .toLowerCase()
      .includes(normalizedQuery) ||
    (translateDynamicLabel(c.audience) || "")
      .toLowerCase()
      .includes(normalizedQuery)
  );
}, [filteredCampaigns, searchQuery, translateDynamicLabel]);

const totalPages = Math.max(1, Math.ceil(searchedCampaigns.length / pageSize) || 1);

const selectableCampaignIds = useMemo(
  () => searchedCampaigns.map(c => c.campaign_id),
  [searchedCampaigns]
);

const selectedInScopeCount = useMemo(
  () => selectableCampaignIds.filter(id => selectedCampaigns.includes(id)).length,
  [selectableCampaignIds, selectedCampaigns]
);

const allInScopeSelected =
  selectableCampaignIds.length > 0 &&
  selectedInScopeCount === selectableCampaignIds.length;


useEffect(() => {
  if (selectAllRef.current) {
    // Only show checked when all rows are selected; never show indeterminate state.
    selectAllRef.current.indeterminate = false;
  }
}, [selectedInScopeCount, selectableCampaignIds.length]);

const columns = [
   {
    title: (
      <input
        ref={selectAllRef}
        type="checkbox"
        checked={allInScopeSelected}
        onChange={e => {
          if (e.target.checked) {
            setSelectedCampaigns(selectableCampaignIds);
          } else {
            setSelectedCampaigns([]);
          }
        }}
      />
    ),
    dataIndex: "select",
    key: "select",
    render: (_: any, record: any) => (
      <input
        type="checkbox"
        checked={selectedCampaigns.includes(record.campaign_id)}
        onChange={e => {
          e.stopPropagation();
          if (e.target.checked) {
            setSelectedCampaigns(prev =>
              prev.includes(record.campaign_id)
                ? prev
                : [...prev, record.campaign_id]
            );
          } else {
            setSelectedCampaigns(prev =>
              prev.filter(id => id !== record.campaign_id)
            );
          }
        }}
      />
    ),
    width: 50,
  },
  {
    title: t.campaignName,
    dataIndex: "campaign_name",
    key: "campaign_name",
    width: 200,
    render: (text: any, record: Campaign) => (
      <span className="campaign-name" dir="auto" style={{ unicodeBidi: "plaintext" }} onClick={() => handleCampaignClick(record)}>
        {translateDynamicLabel(text) || "-"}
      </span>
    ),
  },
  {
    title: t.filterSource,
    key: "source",
    width: 120,
    render: (_: any, record: Campaign) => {
      const source = getCampaignSource(record);
      if (!source) {
        return "-";
      }
      return (
        <span
          className={`campaign-source-badge campaign-source-badge--${normalizeBadgeVariant(source)}`}
        >
          {translateDynamicLabel(source) || formatSourceLabel(source)}
        </span>
      );
    },
  },
  {
    title: t.sentOn,
    dataIndex: "created_at",
    key: "created_at",
    render: (text: any) => (
      <div style={{ whiteSpace: "pre-line" }}>{formatDateTime(text)}</div>
    ),
  },
  {
    title: t.scheduledAt,
    dataIndex: "scheduledAt",
    key: "scheduledAt",
    width: 180,
    render: (text: string | null | undefined) => {
      if (!text || text === "-") {return "-";}
      return <div style={{ whiteSpace: "pre-line" }}>{formatDateTime(text)}</div>;
    },
  },
  {
    title: t.audience,
    dataIndex: "audience",
    key: "audience",
    render: (text: any) => (
      <span dir="auto" style={{ unicodeBidi: "plaintext" }}>
        {translateDynamicLabel(text) || "-"}
      </span>
    ),
  },
  {
    title: t.filterStatus,
    key: "status",
    width: 120,
    render: (_: any, record: Campaign) => {
      const status = getCampaignStatus(record);
      return status ? translateDynamicLabel(status) || formatStatusLabel(status) : "-";
    },
  },
  {
    title: t.actions,
    key: "actions",
    render: (_: any, record: Campaign) => (
      <div
        className="flex items-center space-x-4"
        onClick={e => e.stopPropagation()}
      >
        <CustomIcon
          name={record.status === "archive" ? "unarchive" : "archive"}
          tooltip={
            record.status === "archive" ? t.tooltipUnarchive : t.tooltipArchive
          }
          onClick={() => handleArchiveCampaign(record)}
        />
          <CustomIcon 
          name="delete" 
          tooltip={t.tooltipDelete} 
          onClick={() => handleDeleteCampaign(record)} 
          />
          <CustomIcon
            name="download"
            tooltip={t.tooltipDownload}
            onClick={() => handleExportSingle(record)}
          />
      </div>
    ),
  },
];

  const campaignMetricCells: Array<{
    key: string;
    label: string;
    kind: "count" | "percent" | "unavailable";
    field?: string;
    unavailableNote?: string;
  } | null> = [
    { key: "conversions", label: "Conversions", kind: "count", field: "conversions" },
    { key: "audience", label: "Total Audience", kind: "count", field: "audience_size" },
    { key: "totalUsers", label: "Total Users on Website", kind: "count", field: "total_users" },
    {
      key: "newUsers",
      label: "New Users",
      kind: "unavailable",
      unavailableNote:
        "Not available yet — GA4's new-vs-returning breakdown has no per-campaign dimension.",
    },
    { key: "openRate", label: "Open Rate", kind: "percent", field: "open_rate" },
    { key: "conversionRate", label: "Conversion Rate", kind: "percent", field: "conversion_rate" },
    { key: "emailsSent", label: "Total Emails Sent", kind: "count", field: "delivered" },
    { key: "opens", label: "Opens", kind: "count", field: "opens" },
    {
      key: "repeatedUsers",
      label: "Repeated Users",
      kind: "unavailable",
      unavailableNote:
        "Not available yet — GA4's new-vs-returning breakdown has no per-campaign dimension.",
    },
    { key: "bounceRate", label: "Bounce Rate", kind: "percent", field: "bounce_rate" },
    { key: "clickRate", label: "Click Rate", kind: "percent", field: "click_rate" },
    null,
  ];

  const renderCampaignMetricsPanel = (record: Campaign) => (
    <div className="campaign-metrics-panel">
      {campaignMetricCells.map((cell, index) => {
        if (!cell) {
          return (
            <div
              key={`empty-${index}`}
              className="campaign-metrics-panel__cell campaign-metrics-panel__cell--empty"
              aria-hidden
            />
          );
        }
        const rawValue = Number(record[cell.field as string] ?? 0);
        const value =
          cell.kind === "unavailable"
            ? "—"
            : cell.kind === "percent"
              ? formatPercentValue(rawValue)
              : formatInteger(rawValue);
        return (
          <div key={cell.key} className="campaign-metrics-panel__cell">
            <span className="campaign-metrics-panel__label">
              {cell.label}
              <DefinitionTooltip
                label={cell.label}
                info={{
                  description:
                    cell.kind === "unavailable"
                      ? cell.unavailableNote ?? "Not available from current data sources."
                      : `${cell.label} for this campaign.`,
                }}
              />
            </span>
            <span className="campaign-metrics-panel__value">{value}</span>
          </div>
        );
      })}
    </div>
  );

  // Effects
useEffect(() => {
  fetchCampaignsOverview(startDate, endDate);
}, [i18n.resolvedLanguage, i18n.language]);

  useEffect(() => {
    document.body.style.overflowY = "auto";
    return () => {
      document.body.style.overflowY = "unset";
    };
  }, []);

  // Narrowing the result set can leave the current page past the end of it.
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, showArchived, campaignFilter, sourceFilter, statusFilter]);

  // Source/Status/comparison options are scope-derived, so a value carried over
  // from the other tab would filter everything out. Clear them on tab change.
  useEffect(() => {
    setSourceFilter("");
    setStatusFilter("");
    setComparisonIds([]);
    setComparisonSources([]);
  }, [showArchived]);

  const { title, icon, confirmText, loadingText } = getModalProps(
    actionType || "",
    t,
  );
  const actionLabel =
    actionType === "delete"
      ? t.delete
      : actionType === "archive"
        ? t.archive
        : t.unarchive;
  const confirmDescription = (() => {
    if (!actionType) {return "";}
    if (pendingActionIds.length > 1) {
      return t.confirmMany
        .replace("{{action}}", actionLabel)
        .replace("{{count}}", String(pendingActionIds.length));
    }
    const singleId =
      pendingActionIds[0] || selectedCampaign?.campaign_id || "";
    const singleName =
      translateDynamicLabel(selectedCampaign?.campaign_name) ||
      translateDynamicLabel(campaigns.find(c => c.campaign_id === singleId)?.campaign_name) ||
      t.thisCampaign;
    return t.confirmOne
      .replace("{{action}}", actionLabel)
      .replace("{{name}}", singleName);
  })();
  return (
    <div className="campaign-dashboard" dir={isArabic ? "rtl" : "ltr"}>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          <div className="dashboard-header">
            <div className="campaign-dashboard__title-row">
              <div className="campaign-dashboard__title-copy">
                <p className="Campaign-dashboard-header">{t.campaignPerformance}</p>
                <div className="audience-summary">
                  <span>{t.trackEmailCampaigns}</span>
                </div>
              </div>
              <button
                className="button button--primary campaign-dashboard__create-btn"
                onClick={() => navigate("/account/engagement/create-campaign")}
              >
                <CirclePlus size={16} />
                {t.createNewCampaign}
              </button>
              <div className="campaign-dashboard__filter-select">
                <SelectMenu
                  value={campaignFilter}
                  options={campaignNameOptions}
                  onChange={setCampaignFilter}
                  ariaLabel={t.filterCampaignName}
                  className="campaign-dashboard__select"
                  dropdownClassName="campaign-dashboard__select-dropdown"
                />
              </div>
              <div className="campaign-dashboard__filters">
                <DateRangePicker
                  startDate={startDate}
                  endDate={endDate}
                  maxPastMonths={6}
                  onRangeChange={({ start, end }) => {
                    setStartDate(start);
                    setEndDate(end);
                    fetchCampaignsOverview(start, end);
                  }}
                />
              </div>
            </div>

           
          </div>

          <DashboardNarrativePanel
            narrative={narrative}
            loading={narrativeLoading}
            aiSummary={aiSummary}
            defaultCollapsed={false}
          />

          <div className="campaign-dashboard__achievements">
            <BusinessImpactSection
              metrics={businessImpact}
              subtitle={t.achievementsSubtitle}
              trendFractionDigits={2}
            />
          </div>

          <div className="campaign-performance-card">
            <div className="campaign-card-header">
              <div className="campaign-card-title">
                {showArchived ? t.archivedCampaigns : t.activeCampaigns}
              </div>
              <div className="campaign-card-actions">
                <button
                  className="campaign-action-btn"
                  type="button"
                  onClick={handleExport}
                >
                  <Download size={15} />
                  {t.export}
                </button>
                <button
                  className="campaign-action-btn"
                  type="button"
                  onClick={handleBulkArchive}
                >
                  <Archive size={15} />
                  {showArchived ? t.unarchive : t.archive}
                </button>
                <button
                  className="campaign-action-btn campaign-action-btn--danger"
                  type="button"
                  onClick={handleBulkDelete}
                >
                  <Trash2 size={15} />
                  {t.delete}
                </button>
              </div>
            </div>

            <div className="campaign-card-toolbar">
              <div className="campaign-toolbar-left">
                <div
                  className="campaign-scope-tabs"
                  role="tablist"
                  aria-label={t.activeCampaigns}
                >
                  <button
                    type="button"
                    role="tab"
                    aria-selected={!showArchived}
                    className={`campaign-scope-tab ${!showArchived ? "is-active" : ""}`}
                    onClick={() => setShowArchived(false)}
                  >
                    {t.tabActive}
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={showArchived}
                    className={`campaign-scope-tab ${showArchived ? "is-active" : ""}`}
                    onClick={() => setShowArchived(true)}
                  >
                    {t.tabArchive}
                  </button>
                </div>
              </div>
              <div className="campaign-toolbar-right">
                <StyledInput
                  placeholder={t.search}
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="campaign-search-input"
                />
                <SelectMenu
                  value={sourceFilter}
                  options={sourceOptions}
                  onChange={setSourceFilter}
                  ariaLabel={t.filterSource}
                  className="campaign-dashboard__select campaign-dashboard__select--toolbar"
                  dropdownClassName="campaign-dashboard__select-dropdown"
                />
                <SelectMenu
                  value={statusFilter}
                  options={statusOptions}
                  onChange={setStatusFilter}
                  ariaLabel={t.filterStatus}
                  className="campaign-dashboard__select campaign-dashboard__select--toolbar"
                  dropdownClassName="campaign-dashboard__select-dropdown"
                />
              </div>
            </div>

            <CampaignTable
              columns={columns}
              data={searchedCampaigns.slice((currentPage - 1) * pageSize, currentPage * pageSize)}
              rowKey="campaign_id"
              loading={loading}
              className="campaign-table"
              expandable={{
                expandedRowRender: renderCampaignMetricsPanel,
                expandRowByClick: false,
                expandIconColumnIndex: 1,
                expandIcon: ({ expanded, onExpand, record }) => (
                  <button
                    type="button"
                    className={`campaign-table__expand-icon ${
                      expanded ? "campaign-table__expand-icon--open" : ""
                    }`}
                    onClick={event => onExpand(record, event)}
                    aria-label={expanded ? "Collapse row" : "Expand row"}
                    aria-expanded={expanded}
                  >
                    <ChevronRight size={16} />
                  </button>
                ),
              }}
              customPagination={
                <nav className="campaign-dashboard__pagination" aria-label={t.activeCampaigns}>
                  <span className="campaign-dashboard__pagination-label">
                    {t.pageOf
                      .replace("{{current}}", formatInteger(currentPage))
                      .replace("{{total}}", formatInteger(totalPages))}
                  </span>
                  <div className="campaign-dashboard__pagination-controls">
                    <button
                      type="button"
                      className="campaign-dashboard__pagination-btn"
                      aria-label={translate("pagination.previousPage")}
                      disabled={currentPage <= 1}
                      onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
                    >
                      <ChevronLeft size={16} aria-hidden />
                    </button>
                    <button
                      type="button"
                      className="campaign-dashboard__pagination-btn"
                      aria-label={translate("pagination.nextPage")}
                      disabled={currentPage >= totalPages}
                      onClick={() => setCurrentPage(page => Math.min(totalPages, page + 1))}
                    >
                      <ChevronRight size={16} aria-hidden />
                    </button>
                  </div>
                </nav>
              }
            />
          </div>

          <div className="campaign-chart-card">
            <div className="campaign-chart-header">
              <div className="campaign-chart-title">
                <span>{t.campaignComparison}</span>
                <DefinitionTooltip
                  label={t.campaignComparison}
                  info={getEngagementTooltipInfo(translate, "campaignPerformance")}
                />
              </div>
              <div className="campaign-chart-header-side">
                <div className="campaign-chart-controls">
                  <StyledDropdown
                    mode="multiple"
                    placeholder={t.filterAllCampaigns}
                    value={comparisonIds}
                    onChange={(value: string[]) => setComparisonIds(value ?? [])}
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    maxTagCount={3}
                    maxTagTextLength={12}
                    virtual={false}
                    className="filter-dropdown campaign-compare-select"
                    popupClassName="filter-dropdown-menu campaign-dashboard__compare-menu"
                    options={comparisonOptions}
                  />
                  {comparisonSourceOptions.length > 0 && (
                    <StyledDropdown
                      mode="multiple"
                      placeholder={t.filterSource}
                      value={comparisonSources}
                      onChange={(value: string[]) => setComparisonSources(value ?? [])}
                      allowClear
                      showSearch={false}
                      maxTagCount={2}
                      maxTagTextLength={12}
                      virtual={false}
                      className="filter-dropdown campaign-compare-source"
                      popupClassName="filter-dropdown-menu campaign-dashboard__compare-menu"
                      options={comparisonSourceOptions}
                    />
                  )}
                </div>
                <div className="chart-legend">
                  {selectedMetrics.map(metricKey => {
                    const option = metricOptions.find(opt => opt.value === metricKey);
                    return (
                      <div key={metricKey} className="chart-legend-item">
                        <span
                          className={`chart-legend-dot chart-legend-dot--${metricKey}`}
                        />
                        <span className="chart-legend-label">
                          {option?.label || metricKey}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <MultiMetricBarChart
              chartData={filteredChartData}
              metrics={selectedMetrics.map(metricKey => ({
                key: metricKey,
                label: metricOptions.find(opt => opt.value === metricKey)?.label || metricKey,
              }))}
              tickFormatter={(value: number) => formatPercentValue(value, 0)}
              formatTooltip={(value: number) => formatPercentValue(value, 2)}
            />
          </div>

          <ConfirmationModal
            modalOpen={modalOpen}
            onCancel={handleCancel}
            onConfirm={handleConfirm}
            title={title}
            description={confirmDescription}
            icon={icon}
            confirmText={
              actionLoading ? (
                <>
                  <SyncOutlined spin /> {loadingText}
                </>
              ) : (
                confirmText
              )
            }
            cancelText={t.cancel}
          />
          <CampaignPerformance
            open={Boolean(performanceCampaign)}
            onClose={() => setPerformanceCampaign(null)}
            campaignId={performanceCampaign?.campaign_id}
            campaignName={performanceCampaign?.campaign_name}
          />
        </>
        
      )}
    </div>
  );
};

export default CampaignDashboard;
